/**
 * Moteur de vidéos de cours : tout ce qui est calculé à partir d'une leçon et de sa narration, sans dépendance
 * à React ni au serveur. Partagé par l'API (voyants, synthèse), le studio (aperçu) et le poste de rendu.
 *
 * Vocabulaire : une étape de leçon a une **narration** (texte dit par la voix). Chaque ligne de la narration est un
 * **temps** (beat) ; une narration d'un seul bloc est découpée phrase par phrase. À chaque temps, des éléments apparaissent à l'écran (**cues**). Les cues sont déduites de la leçon
 * (une phrase dite = une phrase affichée, la figure se trace au premier temps, la réponse d'une question se révèle au
 * dernier) ; l'enseignant peut les régler lui-même dans « Plus d'options ».
 */
import type { LessonContent } from "./content.js";
import { initialGeometryParams, readGeometrySpec, stageConstruction } from "./geometry.js";
import { SolidSpec } from "./scene3d.js";

export const VIDEO_STYLES = ["presentation", "tableau", "observation"] as const;
export type VideoStyle = (typeof VIDEO_STYLES)[number];
export const VIDEO_STYLE_LABELS: Record<VideoStyle, string> = { presentation: "Présentation", tableau: "Tableau", observation: "Observation" };
export const VIDEO_FORMATS = ["16:9", "9:16"] as const;
export type VideoFormat = (typeof VIDEO_FORMATS)[number];
export const VIDEO_FORMAT_LABELS: Record<VideoFormat, string> = { "16:9": "Écran", "9:16": "Téléphone" };

export const FPS = 30;
const sec = (s: number) => Math.round(s * FPS);
export const INTRO_FRAMES = sec(2.6);
export const OUTRO_FRAMES = sec(3);

/** Rythme : silences et densité admise, selon le niveau des élèves. La voix elle-même garde sa vitesse. */
export const RHYTHMS = ["lent", "normal", "rapide"] as const;
export type Rhythm = (typeof RHYTHMS)[number];
export type RhythmProfile = {
  label: string;
  /** Silence après chaque phrase dite (s). */
  beatGap: number;
  /** Temps de réflexion avant la réponse d'une question (s). */
  quizPause: number;
  /** Silence à la fin d'une étape (s). */
  stepTail: number;
  /** Mots par phrase et par étape au-delà desquels on prévient l'enseignant. */
  maxWordsPerBeat: number;
  maxWordsPerStep: number;
};
export const RHYTHM_PROFILES: Record<Rhythm, RhythmProfile> = {
  lent: { label: "Lent (6e, 5e)", beatGap: 0.9, quizPause: 5, stepTail: 1.3, maxWordsPerBeat: 22, maxWordsPerStep: 90 },
  normal: { label: "Normal (4e à Tle)", beatGap: 0.4, quizPause: 3, stepTail: 0.9, maxWordsPerBeat: 30, maxWordsPerStep: 130 },
  rapide: { label: "Rapide (révision)", beatGap: 0.3, quizPause: 2, stepTail: 0.6, maxWordsPerBeat: 36, maxWordsPerStep: 170 },
};
/** Niveau interne commun (CI = 1 … 6e = 7, 5e = 8, 4e = 9, 3e = 10, Tle = 13) → rythme conseillé. */
export const rhythmForGrade = (ordinal: number | null | undefined): Rhythm => (ordinal !== null && ordinal !== undefined && ordinal <= 8 ? "lent" : "normal");

// --- Cues -------------------------------------------------------------------------------------------------

export type Cue =
  | { kind: "block"; id: string }
  | { kind: "sentence"; id: string; n: number }
  | { kind: "figure"; id: string; action: "show" | "draw" }
  | { kind: "figure"; id: string; action: "point"; x: number }
  | { kind: "figure"; id: string; action: "params"; params: Record<string, number> }
  | { kind: "figure"; id: string; action: "mark"; x: number; label: string }
  /** Figure de géométrie : cet élément de la construction se trace. */
  | { kind: "figure"; id: string; action: "drawEl"; target: string }
  | { kind: "quiz"; id: string; action: "reveal" }
  /** Modèle 3D ou solide : apparaît, la caméra s'approche d'un point d'intérêt, une partie est mise en avant. */
  | { kind: "model"; id: string; action: "show" }
  | { kind: "model"; id: string; action: "hotspot" | "part"; target: string };

/**
 * Syntaxe d'une cue, lisible par un enseignant :
 *   block:<id> · sentence:<id>:<n> · figure:<id>:show|draw · figure:<id>:point:<x> · figure:<id>:params:a=1,b=2 ·
 *   figure:<id>:mark:<x>:<étiquette> · figure:<id>:draw:<élément> (géométrie) · quiz:<id>:reveal · model:<id>:show ·
 *   model:<id>:hotspot:<point> · model:<id>:part:<partie>
 */
export function parseCue(s: string): Cue | null {
  const [kind, id, a, b, c] = s.trim().split(":");
  if (!id) return null;
  if (kind === "model") {
    if (a === "show") return { kind, id, action: a };
    const target = s.trim().split(":").slice(3).join(":").trim(); // une partie peut contenir des deux-points
    if ((a === "hotspot" || a === "part") && target) return { kind, id, action: a, target };
    return null;
  }
  if (kind === "block") return { kind, id };
  if (kind === "sentence") return { kind, id, n: Number(a) || 0 };
  if (kind === "quiz" && a === "reveal") return { kind, id, action: a };
  if (kind === "figure") {
    if (a === "draw" && b) return { kind, id, action: "drawEl", target: s.trim().split(":").slice(3).join(":") };
    if (a === "show" || a === "draw") return { kind, id, action: a };
    if (a === "point" && Number.isFinite(Number(b))) return { kind, id, action: a, x: Number(b) };
    if (a === "mark" && Number.isFinite(Number(b))) return { kind, id, action: a, x: Number(b), label: c ?? "" };
    if (a === "params") {
      const params: Record<string, number> = {};
      for (const kv of (b ?? "").split(",")) { const [k, v] = kv.split("="); if (k && v !== undefined && Number.isFinite(Number(v))) params[k.trim()] = Number(v); }
      return { kind, id, action: a, params };
    }
  }
  return null;
}
export const cueToString = (c: Cue): string => {
  switch (c.kind) {
    case "block": return `block:${c.id}`;
    case "sentence": return `sentence:${c.id}:${c.n}`;
    case "quiz": return `quiz:${c.id}:reveal`;
    case "model": return c.action === "show" ? `model:${c.id}:show` : `model:${c.id}:${c.action}:${c.target}`;
    case "figure":
      if (c.action === "point") return `figure:${c.id}:point:${c.x}`;
      if (c.action === "mark") return `figure:${c.id}:mark:${c.x}:${c.label}`;
      if (c.action === "drawEl") return `figure:${c.id}:draw:${c.target}`;
      if (c.action === "params") return `figure:${c.id}:params:${Object.entries(c.params).map(([k, v]) => `${k}=${v}`).join(",")}`;
      return `figure:${c.id}:${c.action}`;
  }
};

// --- Narration → temps -------------------------------------------------------------------------------------

export type VideoLesson = Pick<LessonContent, "blocks" | "steps">;
/** Phrases d'un texte (Markdown minimal conservé tel quel). */
export const sentences = (text: string) => text.split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter(Boolean);
/**
 * Temps d'une narration : une ligne = un temps (la voix enchaîne les phrases d'une ligne naturellement).
 * Une narration écrite d'un seul bloc est découpée phrase par phrase.
 */
export function narrationLines(text: string): string[] {
  const lines = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  return lines.length === 1 ? sentences(lines[0]!) : lines;
}
export const wordCount = (text: string) => text.split(/\s+/).filter(Boolean).length;

/** Un temps de narration : une phrase dite (ou un silence), et ce qui apparaît à ce moment. */
export type Beat = { say?: string; pause?: number; cues: string[] };

/** Cues déduites de la leçon pour une étape dont la narration compte `n` phrases. */
export function defaultCues(lesson: VideoLesson, stepIndex: number, n: number): string[][] {
  const step = lesson.steps[stepIndex];
  if (!step || n <= 0) return [];
  const byId = new Map(lesson.blocks.map((b) => [b.id, b]));
  const first: string[] = [];
  const units: string[] = [];
  const hotspots: { cue: string; label: string }[] = []; // points d'intérêt des modèles 3D et repères des images
  const construction: { cue: string; beat: number }[] = []; // éléments d'une figure de géométrie, calés sur les phrases qui les nomment
  const lines = narrationLines(step.narration ?? "");
  let quizId: string | null = null;
  for (const id of step.blockIds) {
    const b = byId.get(id);
    if (!b) continue;
    switch (b.type) {
      case "text": sentences(b.md).forEach((_, i) => units.push(`sentence:${id}:${i}`)); break;
      case "scene3d":
        first.push(`model:${id}:show`);
        for (const h of b.hotspots ?? []) hotspots.push({ cue: `model:${id}:hotspot:${h.id}`, label: h.label.fr ?? h.id });
        break;
      case "image":
        first.push(`model:${id}:show`);
        for (const m of b.markers ?? []) hotspots.push({ cue: `model:${id}:hotspot:${m.id}`, label: m.label.fr ?? m.id });
        break;
      case "solid3d":
        first.push(`model:${id}:show`);
        if (step.figureParams && Object.keys(step.figureParams).length) first.push(cueToString({ kind: "figure", id, action: "params", params: step.figureParams }));
        break;
      case "geometry": {
        first.push(`figure:${id}:show`);
        if (step.figureParams && Object.keys(step.figureParams).length) first.push(cueToString({ kind: "figure", id, action: "params", params: step.figureParams }));
        const g = readGeometrySpec(b.spec);
        if (g) {
          const staged = stageConstruction(g, lines.length === n ? lines : Array.from({ length: n }, () => ""));
          for (const e of g.elements) if (!e.hidden) construction.push({ cue: `figure:${id}:draw:${e.id}`, beat: staged.get(e.id) ?? 0 });
        }
        break;
      }
      case "figure":
        first.push(`figure:${id}:show`, `figure:${id}:draw`);
        if (step.figureParams && Object.keys(step.figureParams).length) first.push(cueToString({ kind: "figure", id, action: "params", params: step.figureParams }));
        break;
      case "quiz": units.push(`block:${id}`); quizId = quizId ?? id; break;
      default: units.push(`block:${id}`);
    }
  }
  const out: string[][] = Array.from({ length: n }, () => []);
  out[0]!.push(...first);
  // Les éléments se répartissent sur les temps dans l'ordre ; la réponse d'une question se révèle au dernier temps.
  const spread = quizId && n > 1 ? n - 1 : n;
  units.forEach((u, k) => { out[Math.min(spread - 1, Math.floor((k * spread) / units.length))]!.push(u); });
  // Points d'intérêt et repères : au temps dont la phrase les nomme (« la membrane », « les mitochondries ») ; les autres
  // se répartissent dans l'ordre sur les temps après le premier (la voix présente d'abord le modèle ou l'image).
  const from = n > 1 ? 1 : 0;
  const norm = (x: string) => x.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const said = lines.length === n ? lines.map(norm) : [];
  const unnamed: string[] = [];
  for (const h of hotspots) {
    const key = norm(h.label).replace(/s$/, "");
    const at = key.length >= 3 ? said.findIndex((l, i) => i >= from && l.includes(key)) : -1;
    if (at >= 0 && at < spread) out[at]!.push(h.cue); else unnamed.push(h.cue);
  }
  unnamed.forEach((h, k) => { out[Math.min(spread - 1, from + Math.floor((k * (spread - from)) / unnamed.length))]!.push(h); });
  // La construction se trace au fil de la voix : chaque élément au temps qui le nomme (avant la réponse d'une question).
  for (const c of construction) out[Math.min(spread - 1, c.beat)]!.push(c.cue);
  if (quizId) out[n - 1]!.push(`quiz:${quizId}:reveal`);
  return out;
}

/** Cues écrites par l'enseignant (« <temps>:<cue> », temps à partir de 1) ; `null` si aucune n'est valide. */
function explicitCues(step: { cues?: string[] }, n: number): string[][] | null {
  if (!step.cues?.length) return null;
  const out: string[][] = Array.from({ length: n }, () => []);
  let any = false;
  for (const line of step.cues) {
    const m = /^(\d+)\s*:\s*(.+)$/.exec(line.trim());
    if (!m) continue;
    const i = Math.min(n, Math.max(1, Number(m[1]))) - 1;
    if (parseCue(m[2]!)) { out[i]!.push(m[2]!.trim()); any = true; }
  }
  return any ? out : null;
}

/** Temps d'une étape : ses phrases, avec leurs cues, et le silence de réflexion avant la réponse d'une question. */
export function stepBeats(lesson: VideoLesson, stepIndex: number, rhythm: Rhythm): Beat[] {
  const step = lesson.steps[stepIndex];
  const said = narrationLines(step?.narration ?? "");
  if (!step || !said.length) return [];
  const cues = explicitCues(step, said.length) ?? defaultCues(lesson, stepIndex, said.length);
  const beats: Beat[] = said.map((say, i) => ({ say, cues: cues[i] ?? [] }));
  const reveal = beats.findIndex((b) => b.cues.some((c) => parseCue(c)?.kind === "quiz"));
  if (reveal > 0) beats.splice(reveal, 0, { pause: RHYTHM_PROFILES[rhythm].quizPause, cues: [] });
  return beats;
}

/** Textes à synthétiser pour une leçon (sans doublon). */
export const narrationTexts = (lesson: VideoLesson, rhythm: Rhythm) =>
  [...new Set(lesson.steps.flatMap((_, i) => stepBeats(lesson, i, rhythm).flatMap((b) => (b.say ? [b.say] : []))))];

/** Voyants d'une étape, avant de générer : ce qui manque ou ce qui est trop dense pour le niveau. */
export function narrationIssues(lesson: VideoLesson, stepIndex: number, rhythm: Rhythm): string[] {
  const step = lesson.steps[stepIndex];
  if (!step) return [];
  const p = RHYTHM_PROFILES[rhythm];
  const out: string[] = [];
  const said = narrationLines(step.narration ?? "");
  if (!said.length) return ["narration manquante"];
  const words = wordCount(step.narration ?? "");
  if (words > p.maxWordsPerStep) out.push(`trop longue pour ce rythme (${words} mots, ${p.maxWordsPerStep} conseillés) : couper l'étape en deux`);
  const long = said.filter((s) => wordCount(s) > p.maxWordsPerBeat).length;
  if (long) out.push(`${long} temps trop long${long > 1 ? "s" : ""} (plus de ${p.maxWordsPerBeat} mots) : une idée par ligne`);
  for (const line of step.cues ?? []) {
    const m = /^(\d+)\s*:\s*(.+)$/.exec(line.trim());
    if (!m || !parseCue(m[2]!)) out.push(`mise en scène illisible : « ${line} »`);
    else if (Number(m[1]) > said.length) out.push(`mise en scène au temps ${m[1]}, mais la narration n'a que ${said.length} temps`);
  }
  return out;
}

// --- Narration synthétisée → chronologie --------------------------------------------------------------------

/** Un temps après synthèse : fichier audio (URL ou clé) et durée mesurée. */
export type SpokenBeat = Beat & { audio?: string; durationSec: number };
export type SpokenNarration = { lang: string; voice: string; steps: Record<string, { beats: SpokenBeat[] }> };

export type TimedBeat = { from: number; frames: number; audio?: string; say?: string; cues: Cue[] };
export type TimedStep = { id: string; index: number; title: string; from: number; frames: number; beats: TimedBeat[] };
export type Timeline = { intro: { from: number; frames: number }; steps: TimedStep[]; outro: { from: number; frames: number }; total: number };

const TITLE_LEAD = sec(0.7); // le titre de l'étape s'affiche avant la première phrase

export function buildTimeline(lesson: VideoLesson, narration: SpokenNarration, lang: string, rhythm: Rhythm = "normal"): Timeline {
  const p = RHYTHM_PROFILES[rhythm];
  let cursor = INTRO_FRAMES;
  const steps: TimedStep[] = lesson.steps.map((step, index) => {
    const from = cursor;
    let t = from + TITLE_LEAD;
    const beats: TimedBeat[] = (narration.steps[step.id]?.beats ?? []).map((b) => {
      const frames = sec(b.durationSec) + (b.say ? sec(p.beatGap) : 0);
      const beat: TimedBeat = { from: t, frames, audio: b.audio, say: b.say, cues: (b.cues ?? []).map(parseCue).filter((c): c is Cue => !!c) };
      t += frames;
      return beat;
    });
    if (beats.length === 0) t += sec(4); // étape sans narration : 4 s à l'écran
    cursor = t + sec(p.stepTail);
    return { id: step.id, index, title: step.title[lang] ?? step.title.fr ?? "", from, frames: cursor - from, beats };
  });
  return { intro: { from: 0, frames: INTRO_FRAMES }, steps, outro: { from: cursor, frames: OUTRO_FRAMES }, total: cursor + OUTRO_FRAMES };
}

/** État de la figure à un instant : paramètres (avec glissement), tracé, point, repères. */
export type FigureState = {
  shownAt: number | null;
  drawAt: number | null;
  point: { x: number; at: number } | null;
  params: { from: Record<string, number>; to: Record<string, number>; at: number };
  marks: { x: number; label: string; at: number }[];
  /** Géométrie : image où chaque élément commence à se tracer ; vide = toute la construction depuis drawAt ou shownAt. */
  elements: { id: string; at: number }[];
};
/** État d'un modèle 3D : visible depuis, point d'intérêt visé (la caméra s'en approche), partie mise en avant, points déjà nommés. */
export type ModelState = {
  shownAt: number | null;
  focus: { hotspotId: string; at: number } | null;
  part: { partId: string; at: number } | null;
  /** Points nommés jusqu'ici (étiquettes qui restent), et tous les mouvements de caméra dans l'ordre (pour les enchaîner). */
  named: { hotspotId: string; at: number }[];
  history: { hotspotId: string; at: number }[];
};
export type SceneState = {
  /** Image (globale) d'apparition de chaque bloc, ou de chaque phrase « id:n ». */
  reveals: Map<string, number>;
  figures: Map<string, FigureState>;
  models: Map<string, ModelState>;
  quizReveals: Map<string, number>;
};

/**
 * État de la scène à une image donnée, construit en rejouant toutes les cues passées (les figures se
 * transmettent d'une étape à l'autre : la droite tracée à l'étape 4 reste tracée à l'étape 5).
 */
export function sceneStateAt(lesson: VideoLesson, tl: Timeline, frame: number): SceneState {
  const reveals = new Map<string, number>();
  const figures = new Map<string, FigureState>();
  const models = new Map<string, ModelState>();
  const quizReveals = new Map<string, number>();
  const blockById = new Map(lesson.blocks.map((b) => [b.id, b]));
  const modelOf = (id: string): ModelState => {
    let m = models.get(id);
    if (!m) { m = { shownAt: null, focus: null, part: null, named: [], history: [] }; models.set(id, m); }
    return m;
  };
  const figureOf = (id: string): FigureState => {
    let f = figures.get(id);
    if (!f) {
      const b = blockById.get(id);
      const g = b?.type === "geometry" ? readGeometrySpec(b.spec) : null;
      const solid = b?.type === "solid3d" ? SolidSpec.safeParse(b.spec).data : undefined;
      const init = b?.type === "figure" ? { ...(b.params ?? {}) } : b?.type === "geometry" ? { ...(g ? initialGeometryParams(g) : {}), ...(b.params ?? {}) } : solid ? Object.fromEntries(solid.sliders.map((x) => [x.name, x.initial])) : {};
      f = { shownAt: null, drawAt: null, point: null, params: { from: init, to: init, at: 0 }, marks: [], elements: [] };
      figures.set(id, f);
    }
    return f;
  };
  for (const step of tl.steps) {
    if (step.from > frame) break;
    const lessonStep = lesson.steps[step.index];
    if (!lessonStep) continue;
    // Chaque étape révèle ses blocs à son rythme, même un bloc déjà montré à l'étape précédente ; seules les figures
    // et les modèles se transmettent (un modèle déjà présenté reste à l'écran, son point visé est oublié).
    reveals.clear();
    quizReveals.clear();
    for (const m of models.values()) { m.focus = null; m.part = null; }
    const cited = new Set<string>();
    const citedSentences = new Set<string>();
    // « quiz:…:reveal » parle de la réponse, pas de l'apparition : la question reste visible dès le début de l'étape.
    for (const beat of step.beats) for (const cue of beat.cues) { if (cue.kind !== "quiz") cited.add(cue.id); if (cue.kind === "sentence") citedSentences.add(`${cue.id}:${cue.n}`); }
    // Blocs jamais cités par une cue de l'étape : visibles dès le début de l'étape.
    for (const id of lessonStep.blockIds) {
      if (cited.has(id)) continue;
      const b = blockById.get(id);
      if (b?.type === "figure" || b?.type === "geometry") { const f = figureOf(id); if (f.shownAt === null) { f.shownAt = step.from; f.drawAt = step.from; } }
      else if (b?.type === "scene3d" || b?.type === "solid3d" || b?.type === "image") { const m = modelOf(id); m.shownAt ??= step.from; }
      else reveals.set(id, step.from);
    }
    for (const beat of step.beats) {
      if (beat.from > frame) break;
      for (const cue of beat.cues) {
        const at = beat.from;
        if (cue.kind === "block") reveals.set(cue.id, at);
        else if (cue.kind === "sentence") reveals.set(`${cue.id}:${cue.n}`, at);
        else if (cue.kind === "quiz") quizReveals.set(cue.id, at);
        else if (cue.kind === "model") {
          const m = modelOf(cue.id);
          m.shownAt ??= at;
          if (cue.action === "hotspot") { m.focus = { hotspotId: cue.target, at }; m.history.push({ hotspotId: cue.target, at }); if (!m.named.some((n) => n.hotspotId === cue.target)) m.named.push({ hotspotId: cue.target, at }); }
          else if (cue.action === "part") m.part = { partId: cue.target, at };
        }
        else {
          const f = figureOf(cue.id);
          if (f.shownAt === null) f.shownAt = at;
          if (cue.action === "draw") f.drawAt ??= at;
          else if (cue.action === "point") f.point = { x: cue.x, at };
          else if (cue.action === "mark") f.marks = [...f.marks.filter((m) => m.x !== cue.x), { x: cue.x, label: cue.label, at }];
          else if (cue.action === "drawEl") { if (!f.elements.some((x) => x.id === cue.target)) f.elements.push({ id: cue.target, at }); }
          else if (cue.action === "params") {
            const to = { ...f.params.to, ...cue.params };
            // Mêmes valeurs qu'avant (cue déduite de l'étape) : pas de glissement.
            if (Object.keys(to).some((k) => to[k] !== f.params.to[k])) f.params = { from: paramsAt(f, at), to, at };
          }
        }
      }
    }
    // Les phrases jamais citées par une cue de l'étape suivent la dernière phrase citée avant elles
    // (une phrase citée par un temps encore à venir reste cachée jusque-là).
    for (const id of lessonStep.blockIds) {
      const b = blockById.get(id);
      if (b?.type !== "text" || !cited.has(id)) continue;
      let last = reveals.get(id) ?? Number.POSITIVE_INFINITY;
      sentences(b.md).forEach((_, n) => {
        const key = `${id}:${n}`;
        if (citedSentences.has(key)) last = reveals.get(key) ?? Number.POSITIVE_INFINITY;
        else if (Number.isFinite(last)) reveals.set(key, last);
      });
    }
  }
  return { reveals, figures, models, quizReveals };
}

const PARAM_TWEEN = sec(0.9);
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
export function paramsAt(f: FigureState, frame: number): Record<string, number> {
  const k = easeInOut(Math.max(0, Math.min(1, (frame - f.params.at) / PARAM_TWEEN)));
  const out: Record<string, number> = {};
  for (const key of new Set([...Object.keys(f.params.from), ...Object.keys(f.params.to)])) {
    const a = f.params.from[key] ?? f.params.to[key] ?? 0;
    const b = f.params.to[key] ?? a;
    out[key] = a + (b - a) * k;
  }
  return out;
}

export const stepAt = (tl: Timeline, frame: number) => tl.steps.find((s) => frame >= s.from && frame < s.from + s.frames) ?? null;

// --- Sous-titres --------------------------------------------------------------------------------------------

export type Caption = { from: number; to: number; text: string };
const CAPTION_MAX = 72;

/** Morceaux courts d'une phrase (au plus 72 caractères), coupés aux ponctuations. */
export function captionChunks(text: string): string[] {
  const parts = text.split(/(?<=[.!?;:])\s+/).flatMap((p) => (p.length <= CAPTION_MAX ? [p] : p.split(/(?<=,)\s+/)));
  const out: string[] = [];
  for (const p of parts) {
    const last = out[out.length - 1];
    if (last && (last + " " + p).length <= CAPTION_MAX) out[out.length - 1] = last + " " + p;
    else out.push(p);
  }
  return out;
}

/** Sous-titres : chaque temps est découpé en morceaux, répartis au prorata des caractères sur sa durée parlée. */
export function captionsOf(tl: Timeline, rhythm: Rhythm = "normal"): Caption[] {
  const gap = sec(RHYTHM_PROFILES[rhythm].beatGap);
  const out: Caption[] = [];
  for (const step of tl.steps) for (const beat of step.beats) {
    if (!beat.say) continue;
    const cs = captionChunks(beat.say);
    const total = cs.reduce((n, c) => n + c.length, 0);
    const spoken = beat.frames - gap;
    let t = beat.from;
    for (const c of cs) {
      const frames = Math.round((c.length / total) * spoken);
      out.push({ from: t, to: t + frames, text: c });
      t += frames;
    }
  }
  return out;
}

const vttTime = (frame: number) => {
  const ms = Math.round((frame / FPS) * 1000);
  const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(ms % 1000).padStart(3, "0")}`;
};
export const toVtt = (caps: Caption[]) => ["WEBVTT", "", ...caps.map((c, i) => `${i + 1}\n${vttTime(c.from)} --> ${vttTime(c.to)}\n${c.text}\n`)].join("\n");

// --- Texte ---------------------------------------------------------------------------------------------------

/** Mots d'un Markdown minimal (**gras**, *italique*), chacun avec son style : pour l'écriture mot à mot. */
export function mdWords(s: string): { text: string; bold: boolean; italic: boolean }[] {
  const out: { text: string; bold: boolean; italic: boolean }[] = [];
  const re = /\*\*(.+?)\*\*|\*(.+?)\*|([^*]+)/g;
  let m: RegExpExecArray | null;
  let glue = false; // le passage précédent ne finissait pas par une espace : « l'**ordonnée** » reste un seul mot
  while ((m = re.exec(s))) {
    const [bold, italic, plain] = [m[1], m[2], m[3]];
    const run = bold ?? italic ?? plain ?? "";
    const words = run.split(/\s+/);
    words.forEach((w, i) => {
      if (!w) return;
      const prev = out[out.length - 1];
      if (i === 0 && glue && prev && !/^\s/.test(run)) { prev.text += w; prev.bold ||= bold !== undefined; prev.italic ||= italic !== undefined; }
      else out.push({ text: w, bold: bold !== undefined, italic: italic !== undefined });
    });
    glue = !/\s$/.test(run);
  }
  return out;
}
